/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Divider from '@material-ui/core/Divider';
import {makeStyles} from '@material-ui/core/styles';
import dynamic from 'next/dynamic';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import Device from '@ion/core/src/services/Device';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {isSideSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getCVSPaginatedAssets} from '@/store/actions/tracking';
import {
  assetDetailsLoadingSelector,
  associatedAssetsIsLoadingSelector,
  findNearbyIsActiveSelector,
  isAsstesCountLoadingSelector,
  isLargeFleetModeSelector,
  largeFleetAssetRefreshSelector,
  largeFleetAssetsSelector,
  sideSheetViewSelector
} from '@/store/selectors/tracking-selectors';
import MobileSideSheetContent from './sidesheet/MobileSideSheetContent';
import SidesheetBreadcrumbs from './sidesheet/SidesheetBreadcrumbs';
import SidesheetContent from './sidesheet/SidesheetContent';
import {breadcrumbWidth} from './trackingUtils';

const SidesheetBreadcrumbsNoSSR = dynamic(() => import('./sidesheet/SidesheetBreadcrumbs'), {ssr: false});

interface SideSheetTrackingContentProps {
  assetList?: Device[];
};

export enum AssetDetailTabItems {
  Overview,
  History
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    '@keyframes fadeInEffect' : {
      '0%'   : {opacity: 0},
      '100%' : {opacity: 1}
    },
    sideSheetContent : {
      width                          : 'inherit',
      backgroundColor                : dlsColor('white'),
      overflowX                      : 'hidden',
      overflowY                      : 'hidden',
      [theme.breakpoints.down('sm')] : {
        width     : '100vw',
        overflowX : 'initial'
      }
    },
    loader : {
      height         : 'calc(100vh - 375px)',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    }
  };
});

const SideSheetTrackingContent = ({assetList}: SideSheetTrackingContentProps) => {
  const classes                    = useStyles();
  const isDesktop                  = useIsDesktop();
  const hasMounted                 = useHasMounted();
  const dispatch                   = useDispatch();
  const isLargeFleetMode           = useSelector(isLargeFleetModeSelector);
  const largeFleetAssets           = useSelector(largeFleetAssetsSelector);
  const refresh                    = useSelector(largeFleetAssetRefreshSelector);
  const assetDetailsLoading        = useSelector(assetDetailsLoadingSelector);
  const associatedAssetsIsLoading  = useSelector(associatedAssetsIsLoadingSelector);
  const isAsstesCountLoading       = useSelector(isAsstesCountLoadingSelector);
  const findNearbyIsActive         = useSelector(findNearbyIsActiveSelector);
  const sideSheetView              = useSelector(sideSheetViewSelector);

  // also waits on the header's own loading flag (isAsstesCountLoadingSelector)
  // - it's set/cleared independently of assetDetailsLoading/
  // associatedAssetsIsLoading, so without this the container could finish
  // "loading" and render an empty list before the header's count/label
  // catches up, showing "no results" under a header that still says
  // "loading" ...
  const isLoading      = useMemo(() => {
    return associatedAssetsIsLoading || assetDetailsLoading || isAsstesCountLoading;
  }, [associatedAssetsIsLoading, assetDetailsLoading, isAsstesCountLoading]);

  /* @private
  *
  * loadCVSPaginatedAssets method helps to load paginated assets
  */
  const loadCVSPaginatedAssets = useCallback((startRow: number = 0) => {
    if (!refresh) {
      dispatch(getCVSPaginatedAssets({startRow}) as any);
    }
  }, [refresh]);

  /**
   * @private
   *
   * Loading the Assets when switching to HFM
   * from Normal Mode
   */
  useEffect(() => {
    if (isLargeFleetMode && !largeFleetAssets.length && hasMounted === true) {
      loadCVSPaginatedAssets();
    }
  }, [isLargeFleetMode]);

  // Defines a component to display the label for the Find Nearby on List and Grid (Side Sheet)
  const findNearbyBreadcrumb = useMemo(() => {
    if (findNearbyIsActive) {
      const isSideTable = isSideSheetTableView(sideSheetView);
      const width       = isSideTable ? breadcrumbWidth.table : breadcrumbWidth.sideSheet;
      return <SidesheetBreadcrumbs show={true} showArrow={!isSideTable} width={width} />;
    }
    return null;
  }, [findNearbyIsActive, sideSheetView, isSideSheetTableView]);

  if (hasMounted) {
    const showBreadcrumb = (!findNearbyIsActive || !isDesktop);
    return (
      <div className={classes.sideSheetContent}>
        {isLoading ? (
          <div className={classes.loader}><IonSpinner /></div>
        ) : (
          <>
            {showBreadcrumb && <SidesheetBreadcrumbsNoSSR />}
            <Divider />
            {isDesktop ? (
              <SidesheetContent assetList={assetList} findNearbyBreadcrumb={findNearbyBreadcrumb} />
            ) : (
              <MobileSideSheetContent />
            )}
          </>
        )}
      </div>
    );
  };
  return null;
};

export default memo(SideSheetTrackingContent);
